import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { useSalePage } from '../../contexts/SalePageContext';
import { db } from '../../lib/firebase';
import { collection, query, where, getDocs, doc, updateDoc, orderBy, deleteDoc } from 'firebase/firestore';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft, CreditCard, User, MapPin, Phone, Mail, Package, Calendar, CheckCircle,
  XCircle, Loader, AlertCircle, Trash2, Truck, Clock, X,
} from 'lucide-react';
import SalePageNavbar from '../../components/SalePageNavbar';

const FONT = '"IBM Plex Sans Thai", system-ui, sans-serif';
const HEAD = "'Anuphan', sans-serif";
const INK = '#0E3A40';

interface PaymentNotification {
  id: string;
  salePageSlug: string;
  packageId: string;
  packageName: string;
  packagePrice: string;
  slipUrl: string;
  customerName: string;
  customerAddress: string;
  customerPhone: string;
  customerEmail: string;
  status: 'pending' | 'approved' | 'rejected';
  deliveryStatus?: 'pending' | 'shipped';
  createdAt: Date;
}
interface SalePage { id: string; slug: string; title: string; }

function statusMeta(status: string) {
  if (status === 'approved') return { label: 'อนุมัติแล้ว', bg: 'rgba(0,180,161,0.12)', color: '#0E7C86' };
  if (status === 'rejected') return { label: 'ปฏิเสธ', bg: 'rgba(255,122,69,0.12)', color: '#B3552A' };
  return { label: 'รอตรวจสอบ', bg: 'rgba(58,147,185,0.12)', color: '#276E8A' };
}
function relTime(d: Date) {
  if (!d) return '';
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'เมื่อสักครู่';
  if (diff < 3600) return `${Math.floor(diff / 60)} นาทีที่แล้ว`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ชั่วโมงที่แล้ว`;
  if (diff < 172800) return 'เมื่อวาน';
  return new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', year: 'numeric' }).format(d);
}
function fullDate(d: Date) {
  if (!d) return '-';
  return new Intl.DateTimeFormat('th-TH', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(d);
}

const SalePagePayments: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { isSubscriptionExpired } = useSalePage();
  const [loading, setLoading] = useState(true);
  const [salePages, setSalePages] = useState<SalePage[]>([]);
  const [paymentNotifications, setPaymentNotifications] = useState<PaymentNotification[]>([]);
  const [selectedPayment, setSelectedPayment] = useState<PaymentNotification | null>(null);
  const [updating, setUpdating] = useState(false);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.uid) { setLoading(false); return; }
      try {
        const pagesQuery = query(collection(db, 'salepages'), where('userId', '==', user.uid));
        const pagesSnapshot = await getDocs(pagesQuery);
        const pages = pagesSnapshot.docs.map((d) => ({ id: d.id, slug: d.data().slug, title: d.data().title }));
        setSalePages(pages);
        if (pages.length === 0) { setLoading(false); return; }
        const slugs = pages.map((p) => p.slug);
        const paymentsQuery = query(
          collection(db, 'paymentNotifications'),
          where('salePageSlug', 'in', slugs),
          orderBy('createdAt', 'desc')
        );
        const paymentsSnapshot = await getDocs(paymentsQuery);
        const payments = paymentsSnapshot.docs.map((d) => ({
          id: d.id, ...d.data(), createdAt: d.data().createdAt?.toDate() || new Date(),
        })) as PaymentNotification[];
        setPaymentNotifications(payments);
      } catch (error) {
        console.error('Error fetching payment notifications:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user?.uid]);

  const handleUpdateStatus = async (paymentId: string, newStatus: 'approved' | 'rejected') => {
    try {
      setUpdating(true);
      await updateDoc(doc(db, 'paymentNotifications', paymentId), { status: newStatus });
      setPaymentNotifications((prev) => prev.map((p) => (p.id === paymentId ? { ...p, status: newStatus } : p)));
      if (selectedPayment?.id === paymentId) setSelectedPayment({ ...selectedPayment, status: newStatus });
      alert(newStatus === 'approved' ? '✅ อนุมัติการชำระเงินสำเร็จ' : '❌ ปฏิเสธการชำระเงินสำเร็จ');
    } catch (error) {
      console.error('Error updating payment status:', error);
      alert('เกิดข้อผิดพลาด: ' + (error as Error).message);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeletePayment = async (paymentId: string) => {
    if (!confirm('คุณแน่ใจหรือไม่ว่าต้องการลบรายการนี้?')) return;
    try {
      setUpdating(true);
      await deleteDoc(doc(db, 'paymentNotifications', paymentId));
      setPaymentNotifications((prev) => prev.filter((p) => p.id !== paymentId));
      if (selectedPayment?.id === paymentId) setSelectedPayment(null);
      alert('🗑️ ลบรายการสำเร็จ');
    } catch (error) {
      console.error('Error deleting payment:', error);
      alert('เกิดข้อผิดพลาด: ' + (error as Error).message);
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateDeliveryStatus = async (paymentId: string, deliveryStatus: 'pending' | 'shipped') => {
    try {
      setUpdating(true);
      await updateDoc(doc(db, 'paymentNotifications', paymentId), { deliveryStatus });
      setPaymentNotifications((prev) => prev.map((p) => (p.id === paymentId ? { ...p, deliveryStatus } : p)));
      if (selectedPayment?.id === paymentId) setSelectedPayment({ ...selectedPayment, deliveryStatus });
      alert(deliveryStatus === 'shipped' ? '✅ อัพเดทสถานะเป็น "จัดส่งแล้ว" สำเร็จ' : '📦 อัพเดทสถานะเป็น "รอการจัดส่ง" สำเร็จ');
    } catch (error) {
      console.error('Error updating delivery status:', error);
      alert('เกิดข้อผิดพลาด: ' + (error as Error).message);
    } finally {
      setUpdating(false);
    }
  };

  const filteredPayments = paymentNotifications.filter((p) => (filter === 'all' ? true : p.status === filter));
  const counts = {
    all: paymentNotifications.length,
    pending: paymentNotifications.filter((p) => p.status === 'pending').length,
    approved: paymentNotifications.filter((p) => p.status === 'approved').length,
    rejected: paymentNotifications.filter((p) => p.status === 'rejected').length,
  };
  const isExpired = isSubscriptionExpired();

  const filterBtn = (active: boolean): React.CSSProperties => ({
    flex: 1, fontFamily: HEAD, fontSize: 13.5, fontWeight: 500, padding: '10px 14px', borderRadius: 11,
    border: 0, cursor: 'pointer', whiteSpace: 'nowrap',
    background: active ? 'linear-gradient(120deg, #00B4A1, #3A93B9)' : 'transparent',
    color: active ? '#fff' : '#5B7D80',
  });
  const statCard = (bg: string, borderC: string): React.CSSProperties => ({
    background: bg, border: `1px solid ${borderC}`, borderRadius: 18, padding: 'clamp(16px,2vw,20px)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  });
  const filters: { key: 'all' | 'pending' | 'approved' | 'rejected'; label: string }[] = [
    { key: 'all', label: `ทั้งหมด (${counts.all})` },
    { key: 'pending', label: `รอตรวจสอบ (${counts.pending})` },
    { key: 'approved', label: `อนุมัติแล้ว (${counts.approved})` },
    { key: 'rejected', label: `ปฏิเสธ (${counts.rejected})` },
  ];

  const infoCard: React.CSSProperties = { background: '#F3F8F8', borderRadius: 14, padding: 16 };

  return (
    <div style={{ minHeight: '100vh', background: '#F3F8F8', color: INK, fontFamily: FONT }}>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Anuphan:wght@400;500;600;700&family=IBM+Plex+Sans+Thai:wght@400;500;600&display=swap" rel="stylesheet" />
      </Helmet>

      <SalePageNavbar />

      <main style={{ maxWidth: 1100, margin: '0 auto', padding: 'clamp(22px,3vw,34px) clamp(16px,3vw,32px) clamp(48px,6vw,80px)', display: 'flex', flexDirection: 'column', gap: 'clamp(18px,2.2vw,22px)' }}>
        {/* Title */}
        <div>
          <button type="button" onClick={() => navigate('/dashboard')} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5, color: '#5B7D80', marginBottom: 10, width: 'fit-content', background: 'transparent', border: 0, cursor: 'pointer' }}>
            <ArrowLeft style={{ width: 15, height: 15 }} /> กลับ Dashboard
          </button>
          <h1 style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 'clamp(24px,2.6vw,30px)', margin: '0 0 4px', letterSpacing: '-0.02em' }}>การแจ้งโอนเงิน</h1>
          <p style={{ fontSize: 14, color: '#5B7D80', margin: 0 }}>จัดการและตรวจสอบการแจ้งโอนเงินจากลูกค้า</p>
        </div>

        {/* Expired banner */}
        {isExpired && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 16, padding: '14px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Clock style={{ width: 20, height: 20, color: '#EF4444', flex: '0 0 auto' }} />
              <div style={{ fontSize: 13.5, color: '#B91C1C' }}>แพ็คเก็จหมดอายุแล้ว — ต่ออายุเพื่อตรวจสอบและอนุมัติการแจ้งโอน</div>
            </div>
            <button type="button" onClick={() => navigate('/salepage/renew')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#EF4444', color: '#fff', border: 0, borderRadius: 999, padding: '9px 16px', fontFamily: HEAD, fontWeight: 600, fontSize: 13.5, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <CreditCard style={{ width: 15, height: 15 }} /> ต่ออายุเลย
            </button>
          </div>
        )}

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '60px 0' }}>
            <Loader style={{ width: 40, height: 40, color: '#00B4A1' }} className="animate-spin" />
            <p style={{ color: '#5B7D80' }}>กำลังโหลด...</p>
          </div>
        ) : (
          <>
            {/* Stat cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,190px),1fr))', gap: 14 }}>
              <div style={statCard('#fff', 'rgba(14,58,64,0.08)')}>
                <div><div style={{ fontSize: 12.5, color: '#5B7D80', marginBottom: 6 }}>ทั้งหมด</div><div style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 26 }}>{counts.all}</div></div>
                <span style={{ width: 38, height: 38, borderRadius: 11, background: 'rgba(14,58,64,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CreditCard style={{ width: 18, height: 18, color: '#5B7D80' }} /></span>
              </div>
              <div style={statCard('rgba(58,147,185,0.08)', 'rgba(58,147,185,0.28)')}>
                <div><div style={{ fontSize: 12.5, color: '#276E8A', marginBottom: 6 }}>รอตรวจสอบ</div><div style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 26, color: '#276E8A' }}>{counts.pending}</div></div>
                <span style={{ width: 38, height: 38, borderRadius: 11, background: 'rgba(58,147,185,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><AlertCircle style={{ width: 18, height: 18, color: '#3A93B9' }} /></span>
              </div>
              <div style={statCard('rgba(0,180,161,0.08)', 'rgba(0,180,161,0.28)')}>
                <div><div style={{ fontSize: 12.5, color: '#0E7C86', marginBottom: 6 }}>อนุมัติแล้ว</div><div style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 26, color: '#0E7C86' }}>{counts.approved}</div></div>
                <span style={{ width: 38, height: 38, borderRadius: 11, background: 'rgba(0,180,161,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CheckCircle style={{ width: 18, height: 18, color: '#00B4A1' }} /></span>
              </div>
              <div style={statCard('rgba(255,122,69,0.08)', 'rgba(255,122,69,0.28)')}>
                <div><div style={{ fontSize: 12.5, color: '#B3552A', marginBottom: 6 }}>ปฏิเสธ</div><div style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 26, color: '#B3552A' }}>{counts.rejected}</div></div>
                <span style={{ width: 38, height: 38, borderRadius: 11, background: 'rgba(255,122,69,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><XCircle style={{ width: 18, height: 18, color: '#FF7A45' }} /></span>
              </div>
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: 6, background: '#fff', borderRadius: 16, padding: 5, border: '1px solid rgba(14,58,64,0.06)', flexWrap: 'wrap' }}>
              {filters.map((f) => (
                <button key={f.key} type="button" onClick={() => setFilter(f.key)} style={filterBtn(filter === f.key)}>{f.label}</button>
              ))}
            </div>

            {/* List */}
            {filteredPayments.length === 0 ? (
              <div style={{ background: '#fff', borderRadius: 24, padding: 'clamp(50px,7vw,72px) clamp(24px,4vw,40px)', border: '1px solid rgba(14,58,64,0.06)', boxShadow: '0 30px 60px -48px rgba(14,58,64,0.35)', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 14 }}>
                <span style={{ width: 64, height: 64, borderRadius: 999, background: 'rgba(58,147,185,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CreditCard style={{ width: 28, height: 28, color: '#3A93B9' }} />
                </span>
                <div style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 16.5 }}>ไม่มีรายการแจ้งโอนเงิน</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {filteredPayments.map((p) => {
                  const sp = salePages.find((s) => s.slug === p.salePageSlug);
                  const m = statusMeta(p.status);
                  return (
                    <div key={p.id} onClick={() => setSelectedPayment(p)} style={{ background: '#fff', borderRadius: 18, padding: 'clamp(16px,2vw,20px)', border: '1px solid rgba(14,58,64,0.06)', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, cursor: 'pointer' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <span style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(14,58,64,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}><CreditCard style={{ width: 18, height: 18, color: '#5B7D80' }} /></span>
                        <div>
                          <div style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 15 }}>{p.customerName}</div>
                          <div style={{ fontSize: 12.5, color: '#5B7D80' }}>{sp?.title || p.salePageSlug} · {relTime(p.createdAt)}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <span style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 16 }}>฿{p.packagePrice}</span>
                        <span style={{ fontSize: 11.5, fontWeight: 600, padding: '5px 12px', borderRadius: 999, background: m.bg, color: m.color }}>{m.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>

      {/* Detail modal */}
      {selectedPayment && (() => {
        const m = statusMeta(selectedPayment.status);
        return (
          <div onClick={() => setSelectedPayment(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(14,58,64,0.45)', backdropFilter: 'blur(4px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 24, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 40px 80px -30px rgba(14,58,64,0.5)' }}>
              <div style={{ position: 'sticky', top: 0, background: 'linear-gradient(120deg, #00B4A1, #3A93B9)', color: '#fff', padding: '20px 24px', borderTopLeftRadius: 24, borderTopRightRadius: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <CreditCard style={{ width: 22, height: 22 }} />
                    <span style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 19 }}>รายละเอียดการแจ้งโอน</span>
                  </div>
                  <button type="button" onClick={() => setSelectedPayment(null)} style={{ background: 'rgba(255,255,255,0.16)', border: 0, borderRadius: 999, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff' }}><X style={{ width: 18, height: 18 }} /></button>
                </div>
                <span style={{ display: 'inline-block', marginTop: 14, fontSize: 12, fontWeight: 600, padding: '5px 12px', borderRadius: 999, background: 'rgba(255,255,255,0.2)', color: '#fff' }}>{m.label}</span>
              </div>

              <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
                {/* Package */}
                <div style={{ ...infoCard, background: 'rgba(0,180,161,0.06)', border: '1px solid rgba(0,180,161,0.2)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: HEAD, fontWeight: 600, fontSize: 15, marginBottom: 12 }}><Package style={{ width: 17, height: 17, color: '#00B4A1' }} /> แพ็คเก็จที่สั่งซื้อ</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 14 }}><span style={{ color: '#5B7D80' }}>ชื่อแพ็คเก็จ:</span><span style={{ fontWeight: 600 }}>{selectedPayment.packageName}</span></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 14 }}><span style={{ color: '#5B7D80' }}>ราคา:</span><span style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 20, color: '#0E7C86' }}>฿{selectedPayment.packagePrice}</span></div>
                </div>

                {/* Customer */}
                <div style={{ ...infoCard, background: 'rgba(58,147,185,0.06)', border: '1px solid rgba(58,147,185,0.2)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: HEAD, fontWeight: 600, fontSize: 15, marginBottom: 12 }}><User style={{ width: 17, height: 17, color: '#3A93B9' }} /> ข้อมูลลูกค้า</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div><div style={{ fontSize: 12, color: '#8AA6A8', marginBottom: 2 }}>ชื่อ-นามสกุล</div><div style={{ fontWeight: 600 }}>{selectedPayment.customerName}</div></div>
                    <div><div style={{ fontSize: 12, color: '#8AA6A8', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}><MapPin style={{ width: 13, height: 13 }} /> ที่อยู่</div><div style={{ fontSize: 14 }}>{selectedPayment.customerAddress}</div></div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div><div style={{ fontSize: 12, color: '#8AA6A8', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}><Phone style={{ width: 13, height: 13 }} /> เบอร์โทร</div><a href={`tel:${selectedPayment.customerPhone}`} style={{ fontWeight: 600, color: '#3A93B9' }}>{selectedPayment.customerPhone}</a></div>
                      <div style={{ minWidth: 0 }}><div style={{ fontSize: 12, color: '#8AA6A8', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}><Mail style={{ width: 13, height: 13 }} /> Email</div><a href={`mailto:${selectedPayment.customerEmail}`} style={{ fontWeight: 600, color: '#3A93B9', wordBreak: 'break-all' }}>{selectedPayment.customerEmail}</a></div>
                    </div>
                  </div>
                </div>

                {/* Slip */}
                {selectedPayment.slipUrl && (
                  <div style={infoCard}>
                    <div style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 15, marginBottom: 12 }}>สลิปโอนเงิน</div>
                    <img src={selectedPayment.slipUrl} alt="สลิปโอนเงิน" onClick={() => window.open(selectedPayment.slipUrl, '_blank')} style={{ width: '100%', maxWidth: 360, margin: '0 auto', display: 'block', borderRadius: 12, boxShadow: '0 12px 30px -16px rgba(14,58,64,0.5)', cursor: 'pointer' }} />
                    <p style={{ textAlign: 'center', fontSize: 12.5, color: '#8AA6A8', marginTop: 10 }}>คลิกเพื่อดูภาพขนาดเต็ม</p>
                  </div>
                )}

                {/* Actions */}
                {selectedPayment.status === 'pending' && (
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button type="button" disabled={updating} onClick={() => { handleUpdateStatus(selectedPayment.id, 'approved'); setSelectedPayment(null); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'linear-gradient(120deg, #00B4A1, #3A93B9)', color: '#fff', border: 0, borderRadius: 999, padding: 14, fontFamily: HEAD, fontWeight: 600, fontSize: 15, cursor: 'pointer', opacity: updating ? 0.6 : 1 }}>
                      <CheckCircle style={{ width: 18, height: 18 }} /> อนุมัติ
                    </button>
                    <button type="button" disabled={updating} onClick={() => { handleUpdateStatus(selectedPayment.id, 'rejected'); setSelectedPayment(null); }} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'linear-gradient(120deg, #FF7A45, #E85D2A)', color: '#fff', border: 0, borderRadius: 999, padding: 14, fontFamily: HEAD, fontWeight: 600, fontSize: 15, cursor: 'pointer', opacity: updating ? 0.6 : 1 }}>
                      <XCircle style={{ width: 18, height: 18 }} /> ปฏิเสธ
                    </button>
                  </div>
                )}

                {/* Delivery */}
                {selectedPayment.status === 'approved' && (
                  <div style={{ ...infoCard, background: 'rgba(58,147,185,0.06)', border: '1px solid rgba(58,147,185,0.2)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: HEAD, fontWeight: 600, fontSize: 15, marginBottom: 12 }}><Truck style={{ width: 17, height: 17, color: '#3A93B9' }} /> สถานะการจัดส่ง</div>
                    {selectedPayment.deliveryStatus === 'shipped' ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'linear-gradient(120deg, #00B4A1, #3A93B9)', color: '#fff', borderRadius: 12, padding: 13, fontFamily: HEAD, fontWeight: 600 }}>
                        <CheckCircle style={{ width: 18, height: 18 }} /> จัดส่งแล้ว
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'rgba(255,122,69,0.12)', color: '#B3552A', borderRadius: 12, padding: 11, fontFamily: HEAD, fontWeight: 600 }}>
                          <Package style={{ width: 17, height: 17 }} /> รอจัดส่ง
                        </div>
                        <button type="button" disabled={updating} onClick={() => handleUpdateDeliveryStatus(selectedPayment.id, 'shipped')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'linear-gradient(120deg, #00B4A1, #3A93B9)', color: '#fff', border: 0, borderRadius: 999, padding: 13, fontFamily: HEAD, fontWeight: 600, fontSize: 15, cursor: 'pointer', opacity: updating ? 0.6 : 1 }}>
                          <Truck style={{ width: 17, height: 17 }} /> ทำเครื่องหมายว่าจัดส่งแล้ว
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <button type="button" disabled={updating} onClick={() => handleDeletePayment(selectedPayment.id)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#B91C1C', borderRadius: 999, padding: 12, fontFamily: HEAD, fontWeight: 600, fontSize: 14.5, cursor: 'pointer', opacity: updating ? 0.6 : 1 }}>
                  <Trash2 style={{ width: 17, height: 17 }} /> ลบรายการ
                </button>

                <div style={{ textAlign: 'center', fontSize: 12.5, color: '#8AA6A8', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <Calendar style={{ width: 14, height: 14 }} /> แจ้งโอนเมื่อ {fullDate(selectedPayment.createdAt)}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default SalePagePayments;
